import { Injectable, inject } from '@angular/core';
import { ErrorEventsActions, WsEventsActions } from '../events/events.actions';
import { SphereWSEvent } from '../events/events.reducer';
import { UINotificationActions } from './notification.actions';
import { NotificationsState, selectAllNotifications, selectNotificationCenter, SphereNotification } from './notification.reducer';
import { EventHandlersService } from '../../event-handlers.service';
import { NotificationsAlertService } from '../../notification-alert.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';

import { SphereNotificationUserSettingKey } from '../../../settings/user-settings/+state/settings.models';
import { selectNotificationUserSettings } from '../../../settings/user-settings/+state/settings.selectors';

@Injectable()
export class NotificationEffects {
    private actions$ = inject(Actions);
    private store = inject<Store<NotificationsState>>(Store);
    private notifications = inject(NotificationsAlertService);
    private eventHandlers = inject(EventHandlersService);

    private readonly dismissNotificationTime$ = this.store
        .select(selectNotificationUserSettings)
        .pipe(map(settings => settings[SphereNotificationUserSettingKey.autoDismissTime]));

    createNotificationFromWSEvents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WsEventsActions.createEvent),
            filter(props => this.isCreateNotificationWSEvent(props.event)),
            map(props => {
                const notification = {
                    ...this.eventHandlers.buildNotification(props.event),
                    timestamp: new Date(),
                    visible: true,
                    new: true,
                    read: false
                };
                return UINotificationActions.createNotification({ notification: notification as SphereNotification });
            })
        )
    );

    updateNotificationFromWSEvents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WsEventsActions.createEvent),
            filter(props => this.isUpdateNotificationWSEvent(props.event)),
            withLatestFrom(this.store.select(selectAllNotifications)),
            map(([props, notifications]) => {
                return {
                    props,
                    previousNotification: notifications.find(notification => notification.id === props.event['correlation-id'])
                };
            }),
            filter(({ props, previousNotification }) => {
                return !!previousNotification;
            }),
            map(({ props, previousNotification }) => {
                const notification = {
                    ...this.eventHandlers.buildNotification({
                        ...props.event
                    }),
                    timestamp: new Date(),
                    visible: true,
                    new: true,
                    read: false,
                    autoDismiss: previousNotification?.autoDismiss
                };
                return UINotificationActions.updateNotification({ notification: notification as SphereNotification });
            })
        )
    );

    createNotificationFromErrorEvent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ErrorEventsActions.createErrorEvent),
            map(props => {
                const timestamp = new Date();
                const notification: SphereNotification = {
                    id: timestamp.toISOString(),
                    title: props.error.title || 'Error',
                    content: props.error.message,
                    timestamp,
                    visible: true,
                    new: true,
                    read: false,
                    context: 'error'
                };
                return UINotificationActions.createNotification({ notification });
            })
        )
    );

    createNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UINotificationActions.createNotification),
            withLatestFrom(
                this.store.select(selectNotificationUserSettings).pipe(map(settings => settings[SphereNotificationUserSettingKey.autoDismissTime]))
            ),
            map(([props, autoDismiss]) => {
                return UINotificationActions.displayNotification({ notification: { ...props.notification, autoDismiss } });
            })
        )
    );

    displayNotification$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(UINotificationActions.displayNotification),
                withLatestFrom(this.store.select(selectNotificationCenter)),
                tap(([props, notificationCenter]) => {
                    if (!notificationCenter.open && !notificationCenter.dnd) {
                        this.notifications.open(props.notification);
                    }
                })
            ),
        { dispatch: false }
    );

    private isCreateNotificationWSEvent(event: SphereWSEvent) {
        if (!event || this.eventHandlers.isCorrelationOnlyEvent(event['entity-type'], event.operation)) {
            return false;
        }
        return (
            this.eventHandlers.isSphereNotificationEvent(event) || this.eventHandlers.isCustomNotificationEvent(event['entity-type'], event.operation)
        );
    }

    private isUpdateNotificationWSEvent(event: SphereWSEvent) {
        if (!event) {
            return false;
        }
        return (
            this.eventHandlers.isCustomNotificationEvent(event['entity-type'], event.operation) &&
            this.eventHandlers.isCorrelationOnlyEvent(event['entity-type'], event.operation)
        );
    }
}
